import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { settingsAPI } from "../services/api";

interface Settings {
  soundEnabled: boolean;
  theme: "light" | "dark";
  exerciseOrder: "biblical" | "random" | "devotional";
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  toggleSound: () => void;
  toggleTheme: () => void;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

const defaultSettings: Settings = {
  soundEnabled: true,
  theme: "dark",
  exerciseOrder: "random",
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

// Helper function to apply theme classes immediately
const applyTheme = (theme: "light" | "dark") => {
  const root = window.document.documentElement;

  // Remove previous theme classes
  root.classList.remove("light", "dark");

  // Add current theme class
  root.classList.add(theme);

  // Apply theme to document
  if (theme === "dark") {
    root.style.colorScheme = "dark";
  } else {
    root.style.colorScheme = "light";
  }
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      // If user is not authenticated, use localStorage
      if (!isAuthenticated || authLoading) {
        const saved = localStorage.getItem("greek-cloze-settings");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const mergedSettings = { ...defaultSettings, ...parsed };
            setSettings(mergedSettings);
            applyTheme(mergedSettings.theme);
          } catch (error) {
            console.warn("Failed to parse saved settings:", error);
            applyTheme(defaultSettings.theme);
          }
        } else {
          applyTheme(defaultSettings.theme);
        }
        setLoading(false);
        return;
      }

      try {
        const response = await settingsAPI.getSettings(getAccessTokenSilently);
        const apiSettings = response.settings || {};

        // Merge with defaults
        const mergedSettings = {
          soundEnabled:
            apiSettings.soundEnabled !== undefined
              ? apiSettings.soundEnabled
              : defaultSettings.soundEnabled,
          theme: apiSettings.theme || defaultSettings.theme,
          exerciseOrder:
            apiSettings.exerciseOrder || defaultSettings.exerciseOrder,
        };

        setSettings(mergedSettings);
        // Apply theme immediately to prevent flash
        applyTheme(mergedSettings.theme);
        // Also save to localStorage as backup
        localStorage.setItem(
          "greek-cloze-settings",
          JSON.stringify(mergedSettings)
        );
      } catch (error) {
        console.warn(
          "Failed to load settings from API, using localStorage fallback:",
          error
        );
        // If API fails, try to load from localStorage as fallback
        const saved = localStorage.getItem("greek-cloze-settings");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const mergedSettings = { ...defaultSettings, ...parsed };
            setSettings(mergedSettings);
            applyTheme(mergedSettings.theme);
          } catch (error) {
            console.warn("Failed to parse saved settings:", error);
            // Apply default theme
            applyTheme(defaultSettings.theme);
          }
        } else {
          // Apply default theme immediately
          applyTheme(defaultSettings.theme);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [getAccessTokenSilently, isAuthenticated, authLoading]);

  // Save settings to API whenever they change
  const saveSettings = async (newSettings: Settings) => {
    // Always save to localStorage as backup
    localStorage.setItem("greek-cloze-settings", JSON.stringify(newSettings));

    // If authenticated, also save to API
    if (isAuthenticated && !authLoading) {
      try {
        await settingsAPI.updateSettings(newSettings, getAccessTokenSilently);
      } catch (error) {
        console.warn(
          "Failed to save settings to API (using localStorage):",
          error
        );
      }
    }
  };

  const updateSettings = async (newSettings: Partial<Settings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    await saveSettings(updatedSettings);
  };

  const toggleSound = () => {
    const newSettings = { ...settings, soundEnabled: !settings.soundEnabled };
    setSettings(newSettings);
    // Save to API in background (don't await)
    saveSettings(newSettings).catch((error) => {
      console.warn("Failed to save sound settings:", error);
    });
  };

  const toggleTheme = () => {
    const newTheme = (settings.theme === "light" ? "dark" : "light") as
      | "light"
      | "dark";
    const newSettings = {
      ...settings,
      theme: newTheme,
    };
    setSettings(newSettings);
    // Apply theme immediately to avoid any delay
    applyTheme(newTheme);
    // Save to API in background (don't await to avoid blocking UI)
    saveSettings(newSettings).catch((error) => {
      console.warn("Failed to save theme settings:", error);
    });
  };

  const value = {
    settings,
    updateSettings,
    toggleSound,
    toggleTheme,
    loading,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
